/**
 * ProjectThemePickerDialog - Wizard-style wrapper for selecting a system V2 theme.
 *
 * @module themes/components
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  Button,
  Typography,
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import { useThemesV2 } from '../hooks/useThemesV2';
import type { ThemeV2 } from '../types/themesV2.types';
import { ThemeV2SelectionList } from './ThemeV2SelectionList';
import {
  FULLSCREEN_DIALOG_BUTTON_SX,
  FULLSCREEN_DIALOG_HEADER_SX,
  FULLSCREEN_DIALOG_PAPER_SX,
  FULLSCREEN_DIALOG_TITLE_SX,
} from '@modules/themes/components/fullscreenDialogChrome';

export interface ProjectThemePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (theme: ThemeV2) => void | Promise<void>;
  projectId?: string;
}

export const ProjectThemePickerDialog: React.FC<ProjectThemePickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  projectId,
}) => {
  const { themes, loading, hasMore, loadMore } = useThemesV2({ pageSize: 50 });
  const [selectedTheme, setSelectedTheme] = useState<ThemeV2 | null>(null);
  const [selecting, setSelecting] = useState(false);
  const selectingRef = useRef(false);

  const handleSelect = useCallback((theme: ThemeV2) => {
    setSelectedTheme(theme);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedTheme || selectingRef.current) {
      return;
    }

    selectingRef.current = true;
    setSelecting(true);
    try {
      await onSelect(selectedTheme);
      setSelectedTheme(null);
    } finally {
      selectingRef.current = false;
      setSelecting(false);
    }
  }, [selectedTheme, onSelect]);

  const handleClose = useCallback(() => {
    setSelectedTheme(null);
    selectingRef.current = false;
    setSelecting(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (open && !loading && !selectedTheme && themes.length > 0) {
      setSelectedTheme(themes[0]);
    }
  }, [loading, open, selectedTheme, themes]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      sx={FULLSCREEN_DIALOG_PAPER_SX}
      data-testid="project-theme-picker-dialog"
    >
      <Box component="header" sx={FULLSCREEN_DIALOG_HEADER_SX}>
        <Button
          size="small"
          onClick={handleClose}
          sx={{ ...FULLSCREEN_DIALOG_BUTTON_SX, zIndex: 1 }}
          data-testid="project-theme-picker-cancel-btn"
        >
          Back
        </Button>

        <Typography variant="h6" component="h2" sx={FULLSCREEN_DIALOG_TITLE_SX}>
          Select Theme
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', zIndex: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirm}
            disabled={!selectedTheme || selecting}
            sx={FULLSCREEN_DIALOG_BUTTON_SX}
            data-testid="project-theme-picker-select-btn"
          >
            {selecting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'Select Theme'
            )}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, p: 3 }}>
        <ThemeV2SelectionList
          sources={[{
            id: 'system',
            label: 'System themes',
            themes,
            themeSource: 'global',
            chipLabel: 'Global',
          }]}
          selectedThemeId={selectedTheme?.id}
          projectId={projectId}
          loading={loading}
          hasMore={hasMore}
          loadMore={loadMore}
          loadMoreDisabled={loading}
          emptyMessage="No V2 themes available. Create one in Admin -> Themes V2."
          onSelect={handleSelect}
        />
      </Box>
    </Dialog>
  );
};
